import React, { useEffect, useState, useMemo } from "react";
import {
  CircularProgress,
  Alert,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  TextField,
  Button,
  Box, // Import Box for layout
  FormControl, // Import for dropdown
  InputLabel, // Import for dropdown
  Select, // Import for dropdown
  MenuItem, // Import for dropdown
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import api from "../services/api";

// +++ START OF NEW CODE: Helper function to flatten the hierarchy (reused from previous solution) +++
const flattenHierarchy = (node) => {
  if (!node) {
    return [];
  }
  const list = [{ email: node.email, name: node.name }];
  if (node.reportees && node.reportees.length > 0) {
    for (const reportee of node.reportees) {
      list.push(...flattenHierarchy(reportee));
    }
  }
  return list;
};
// +++ END OF NEW CODE +++

const FilterSummary = () => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false); // Set initial loading to false
  const [error, setError] = useState(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // +++ START OF NEW STATE MANAGEMENT +++
  const [reportees, setReportees] = useState([]);
  const [selectedReporteeIndex, setSelectedReporteeIndex] = useState(0);
  const [initialLoad, setInitialLoad] = useState(true); // To prevent multiple initial fetches
  // +++ END OF NEW STATE MANAGEMENT +++

  // Effect to fetch the list of reportees once on component mount
  useEffect(() => {
    const fetchReportees = async () => {
      try {
        const response = await api.get("users/get_reportees/", {
          headers: {
            Authorization: `Token ${localStorage.getItem("token")}`,
          },
        });

        const hierarchyData = response.data.hierarchy;
        let flatReporteeList = [];

        if (Array.isArray(hierarchyData)) { // Case for "Head" user
          flatReporteeList = hierarchyData.flatMap(head => flattenHierarchy(head));
        } else if (hierarchyData) { // Case for regular user
          flatReporteeList = flattenHierarchy(hierarchyData);
        }
        
        setReportees(flatReporteeList);

      } catch (err) {
        console.error("Failed to fetch reportees:", err);
        setError("Could not load the list of reportees.");
      }
    };

    fetchReportees();
  }, []);

  // Effect to trigger the initial data fetch for the logged-in user (index 0)
  // once the reportees list is populated.
  useEffect(() => {
    if (reportees.length > 0 && initialLoad) {
      fetchSummary();
      setInitialLoad(false); // Prevent this from running again
    }
  }, [reportees, initialLoad]);

  const fetchSummary = async () => {
    // +++ MODIFIED FETCH LOGIC +++
    const selectedUser = reportees[selectedReporteeIndex];
    if (!selectedUser) {
        // If no user is selected yet (e.g., list is still loading), do nothing.
        return;
    }

    setLoading(true);
    setError(null);
    setSummary(null);

    try {
      const clientTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const params = new URLSearchParams();
      
      params.append("email", selectedUser.email); // Use the selected user's email
      params.append("timezone", clientTimezone);
      if (startDate) params.append("start_date", startDate);
      if (endDate) params.append("end_date", endDate);

      const response = await api.get(`timesheet/api/date/?${params.toString()}`);
      setSummary(response.data.today_summary);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to fetch summary");
    } finally {
      setLoading(false);
    }
  };

  const kpis = useMemo(() => {
    if (!summary || Object.keys(summary).length === 0) return null;
    let topTask = { project: "", task: "", minutes: 0 };
    let topProject = { project: "", minutes: 0 };
    let quickCompleteCount = 0;
    let totalMinutes = 0;
    Object.entries(summary).forEach(([project, tasks]) => {
      let projTotal = 0;
      Object.entries(tasks).forEach(([task, minutes]) => {
        totalMinutes += minutes;
        projTotal += minutes;
        if (minutes > topTask.minutes) topTask = { project, task, minutes };
        if (minutes <= 30) quickCompleteCount++;
      });
      if (projTotal > topProject.minutes) topProject = { project, minutes: projTotal };
    });
    return { topTask, topProject, quickCompleteCount, totalMinutes };
  }, [summary]);

  const handleFilterSubmit = () => {
    fetchSummary();
  };

  const copyTableToClipboard = () => {
    if (!summary) return;
    let text = "";
    Object.keys(summary).forEach((project) => {
      text += `\n${project}\n${"─".repeat(40)}\n`;
      Object.keys(summary[project]).forEach((task) => {
        text += `${task}\t${summary[project][task]} min\n`;
      });
    });
    navigator.clipboard.writeText(text.trim()).catch(() => {
      const el = document.createElement("textarea");
      el.value = text.trim();
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
    });
  };

  return (
    <Paper elevation={3} style={{ padding: "24px", maxWidth: "800px", margin: "20px auto" }}>
      <Typography variant="h4" gutterBottom>
        Time Summary
      </Typography>

      {/* +++ START OF NEW UI ELEMENT: REPORTEE DROPDOWN +++ */}
      <FormControl fullWidth variant="outlined" style={{ marginBottom: "20px" }}>
        <InputLabel>Reportee</InputLabel>
        <Select
          value={selectedReporteeIndex}
          onChange={(e) => setSelectedReporteeIndex(e.target.value)}
          label="Reportee"
        >
          {reportees.map((reportee, index) => (
            <MenuItem key={reportee.email} value={index}>
              {reportee.name} ({reportee.email})
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {/* +++ END OF NEW UI ELEMENT +++ */}

      {/* Date Filters */}
      <Box sx={{ display: "flex", gap: 2, marginBottom: "20px", alignItems: 'center' }}>
        <TextField
          type="date"
          label="Start Date"
          InputLabelProps={{ shrink: true }}
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          fullWidth
        />
        <TextField
          type="date"
          label="End Date"
          InputLabelProps={{ shrink: true }}
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          fullWidth
        />
        <Button variant="contained" color="primary" onClick={handleFilterSubmit} sx={{ height: '56px' }}>
          Filter
        </Button>
      </Box>
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : (!summary || Object.keys(summary).length === 0) ? (
        <Alert severity="info">No summary available for the selected criteria.</Alert>
      ) : (
        <>
          {kpis && (
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 3 }}>
              {[
                {
                  label: "Top Project",
                  value: kpis.topProject.project || "—",
                  sub: `${kpis.topProject.minutes} min`,
                  color: "#1565c0",
                },
                {
                  label: "Top Task",
                  value: kpis.topTask.task || "—",
                  sub: `${kpis.topTask.minutes} min · ${kpis.topTask.project}`,
                  color: "#2e7d32",
                },
                {
                  label: "Total Time",
                  value: `${Math.floor(kpis.totalMinutes / 60)}h ${kpis.totalMinutes % 60}m`,
                  sub: `${kpis.totalMinutes} minutes`,
                  color: "#6a1b9a",
                },
                {
                  label: "Quick Completes",
                  value: kpis.quickCompleteCount,
                  sub: "tasks ≤ 30 min",
                  color: "#e65100",
                },
              ].map((kpi) => (
                <Paper
                  key={kpi.label}
                  elevation={2}
                  sx={{
                    flex: "1 1 150px",
                    p: 2,
                    borderLeft: `4px solid ${kpi.color}`,
                    minWidth: 140,
                  }}
                >
                  <Typography variant="caption" color="text.secondary" display="block">
                    {kpi.label}
                  </Typography>
                  <Typography variant="h6" sx={{ color: kpi.color, fontWeight: 700, lineHeight: 1.2 }}>
                    {kpi.value}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {kpi.sub}
                  </Typography>
                </Paper>
              ))}
            </Box>
          )}
          <IconButton onClick={copyTableToClipboard} color="primary" aria-label="copy table" style={{ marginBottom: "10px" }}>
            <ContentCopyIcon />
          </IconButton>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Project</TableCell>
                  <TableCell>Task</TableCell>
                  <TableCell align="right">Duration (minutes)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.keys(summary).map((project, projIndex) =>
                  Object.keys(summary[project]).map((task, taskIndex) => (
                    <TableRow key={`${projIndex}-${taskIndex}`}>
                      {taskIndex === 0 && (
                        <TableCell rowSpan={Object.keys(summary[project]).length}>
                          {project}
                        </TableCell>
                      )}
                      <TableCell>{task}</TableCell>
                      <TableCell align="right">{summary[project][task]}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Paper>
  );
};

export default FilterSummary;